import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { LoadingPage } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { UserRole } from '@/types/api';
import type { User, UserOrgMembership } from '@/types/api';
import { getUserById, getUserOrganizations } from '../api/users-api';
import { addOrgMember, removeOrgMember, getOrganizations } from '@/features/organizations/api/organizations-api';
import { roleLabel } from '@/lib/display-labels';
import type { Organization } from '@/types/api';

const roleVariants: Record<UserRole, 'default' | 'success' | 'warning' | 'error' | 'info' | 'secondary'> = {
  [UserRole.Owner]: 'secondary',
  [UserRole.Admin]: 'info',
  [UserRole.Manager]: 'warning',
  [UserRole.Viewer]: 'default',
};

const ROLE_OPTIONS = [
  { value: UserRole.Viewer, label: 'Viewer' },
  { value: UserRole.Manager, label: 'Manager' },
  { value: UserRole.Admin, label: 'Admin' },
  { value: UserRole.Owner, label: 'Sys Admin' },
];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<UserOrgMembership[]>([]);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const [isAddOrgOpen, setIsAddOrgOpen] = useState(false);
  const [addOrgId, setAddOrgId] = useState('');
  const [addOrgRole, setAddOrgRole] = useState<UserRole>(UserRole.Viewer);
  const [addOrgLoading, setAddOrgLoading] = useState(false);
  const [addOrgError, setAddOrgError] = useState('');

  const [removingMembership, setRemovingMembership] = useState<UserOrgMembership | null>(null);

  useEffect(() => {
    if (!id) return;
    loadAll(id);
  }, [id]);

  async function loadAll(userId: string) {
    setLoading(true);
    try {
      const [userData, membershipData, orgsData] = await Promise.all([
        getUserById(userId),
        getUserOrganizations(userId),
        getOrganizations({ perPage: 100 }),
      ]);
      setUser(userData);
      setMemberships(membershipData);
      setAllOrgs(orgsData.items);
    } catch {
      navigate('/users');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddOrg() {
    if (!id || !addOrgId) return;
    setAddOrgLoading(true);
    setAddOrgError('');
    try {
      const org = allOrgs.find(o => o.id === addOrgId);
      await addOrgMember(addOrgId, { email: user!.email, role: addOrgRole });
      setMemberships(prev => [...prev, {
        organizationId: addOrgId,
        organizationName: org?.name ?? '',
        organizationSlug: org?.slug ?? '',
        role: addOrgRole,
        joinedAt: new Date().toISOString(),
      }]);
      setIsAddOrgOpen(false);
      setAddOrgId('');
      setAddOrgRole(UserRole.Viewer);
    } catch {
      setAddOrgError('Failed to add user to organization.');
    } finally {
      setAddOrgLoading(false);
    }
  }

  async function handleRemoveConfirm() {
    if (!removingMembership || !id) return;
    try {
      await removeOrgMember(removingMembership.organizationId, id);
      setMemberships(prev => prev.filter(m => m.organizationId !== removingMembership.organizationId));
    } catch {
      // swallow, refresh
      loadAll(id);
    } finally {
      setRemovingMembership(null);
    }
  }

  if (loading || !user) return <LoadingPage />;

  const memberOrgIds = new Set(memberships.map(m => m.organizationId));
  const availableOrgs = allOrgs.filter(o => !memberOrgIds.has(o.id));

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
              <ArrowLeft className="w-4 h-4" />
              Users
            </Button>
          </div>

          {/* User Info */}
          <Card variant="elevated">
            <div className="p-6 flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-semibold text-primary-700 dark:text-primary-300">
                  {user.firstName[0]}{user.lastName[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {user.firstName} {user.lastName}
                  </h1>
                  <Badge variant={user.isActive ? 'success' : 'error'} size="sm" dot>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{user.email}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2 text-sm mt-2 sm:mt-0">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Last Login</p>
                  <p className="text-gray-700 dark:text-gray-300">
                    {user.lastLoginAt ? formatRelativeTime(user.lastLoginAt) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Member Since</p>
                  <p className="text-gray-700 dark:text-gray-300">{formatDate(user.createdAt)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Organizations */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Organizations</h2>
              {availableOrgs.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setIsAddOrgOpen(true)}>
                  <Plus className="w-4 h-4" />
                  Add to Organization
                </Button>
              )}
            </div>

            <Card variant="elevated" padding="none">
              {memberships.length === 0 ? (
                <EmptyState
                  icon={<Building2 className="w-10 h-10" />}
                  title="No organizations"
                  description="This user isn't a member of any organization."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberships.map(m => (
                      <TableRow key={m.organizationId}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">{m.organizationName}</p>
                            <p className="text-xs text-gray-400 font-mono">{m.organizationSlug}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleVariants[m.role]} size="sm">{roleLabel[m.role]}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(m.joinedAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-error-600 hover:text-error-700"
                            onClick={() => setRemovingMembership(m)}
                            title="Remove from organization"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Add to Org Modal */}
      <Modal
        isOpen={isAddOrgOpen}
        onClose={() => { setIsAddOrgOpen(false); setAddOrgError(''); setAddOrgId(''); setAddOrgRole(UserRole.Viewer); }}
        title="Add to Organization"
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="Organization"
            value={addOrgId}
            onChange={setAddOrgId}
            options={[
              { value: '', label: 'Select an organization...' },
              ...availableOrgs.map(o => ({ value: o.id, label: o.name })),
            ]}
          />
          <Select
            label="Role"
            value={addOrgRole}
            onChange={(v) => setAddOrgRole(v as UserRole)}
            options={ROLE_OPTIONS}
          />
          {addOrgError && <p className="text-sm text-error-600">{addOrgError}</p>}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setIsAddOrgOpen(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleAddOrg}
              disabled={!addOrgId}
              isLoading={addOrgLoading}
            >
              Add
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove from org confirm */}
      <ConfirmDialog
        isOpen={!!removingMembership}
        title="Remove from Organization"
        description={`Remove ${user.firstName} ${user.lastName} from ${removingMembership?.organizationName}?`}
        confirmLabel="Remove"
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemovingMembership(null)}
      />
    </>
  );
}
